<template>
  <div>
    <!-- Focus Areas: header lives on the page so it reads as part of the
         section; the widget (headerless) renders below it. -->
    <div class="mb-3">
      <div class="ext-section-number">01 / Overview</div>
      <h1 class="ext-section-title">Focus Areas</h1>
    </div>
    <ExternalFocusItems :entity-id="entityId" />

    <LoadingIndicator v-if="loading" message="Loading summary data…" />

    <div v-else-if="chart.length || table.length">
      <div class="mb-8">
        <h1 class="ext-section-title">Summary Analysis</h1>
        <p class="ext-section-desc">Core program metrics derived from CCLF claims, attribution, and observed dates-of-service.</p>
      </div>

      <!-- PBPM Trend Chart -->
      <div v-if="chart.length" class="ext-hero" style="margin-bottom:16px; min-width:0;">
        <div style="display:flex; align-items:baseline; gap:16px; margin-bottom:8px;">
          <div class="ext-hero-label">PBPM Trend</div>
          <span :class="['ext-change', heroChange >= 0 ? 'negative' : 'positive']" v-if="heroChange != null">
            {{ heroChange >= 0 ? '▲' : '▼' }} {{ Math.abs(heroChange).toFixed(1) }}% vs previous quarter
          </span>
        </div>
        <div style="display:grid; grid-template-columns:220px 1fr; gap:16px; align-items:center;">
          <div style="display:flex; flex-direction:column; gap:4px; padding:8px 12px; border:1px solid #E5E7EB; border-radius:8px; box-shadow:0 1px 3px rgba(0,0,0,0.06);">
            <div v-for="item in legendItems" :key="item.key"
              style="display:grid; grid-template-columns:16px 1fr 16px; gap:6px; align-items:center; cursor:pointer; user-select:none; padding:2px 0;"
              @click="toggleSeries(item.key)">
              <input type="checkbox" :checked="!hiddenSeriesSet.has(item.key)"
                style="width:12px; height:12px; margin:0; cursor:pointer; accent-color:#4B5563;"
                @click.stop="toggleSeries(item.key)" />
              <span :style="{ fontSize: '11px', fontWeight: '500', color: hiddenSeriesSet.has(item.key) ? '#9CA3AF' : '#4B5563' }">{{ item.label }}</span>
              <span :style="{ width: '16px', height: '3px', borderRadius: '2px', background: hiddenSeriesSet.has(item.key) ? '#D1D5DB' : item.color, borderStyle: item.dashed ? 'dashed' : 'solid' }"></span>
            </div>
          </div>
          <div style="min-width:0;">
            <CCLFReport_EntityCost :entity-cost-pbpm="entityCostPbpm" :performance-year="performanceYear" :hidden-series="hiddenSeriesList" />
          </div>
        </div>
        <div style="margin-top:10px; font-size:10.5px; color:#9CA3AF; line-height:1.7;">
          Monthly PBPM — CCLF Part A, B, and DME claims ÷ attributed patients. Each quarter uses its own CMS attribution panel.<br/>
          Claims through {{ maxEfctvDt || '—' }}, chart capped 1 month prior.
        </div>
      </div>

      <!-- Total Lives -->
      <div v-if="livesSeries.length" class="ext-hero" style="display:grid; grid-template-columns:180px 1fr; gap:16px; align-items:center; margin-bottom:24px; min-width:0;">
        <div>
          <div class="ext-hero-label">Total Lives · {{ livesSeries.length }} reports</div>
          <div class="ext-hero-value">{{ fmtCount(livesCurrentValue) }}</div>
          <div class="ext-hero-sub" style="margin-top:6px;">
            <span :class="['ext-change', livesDelta >= 0 ? 'positive' : 'negative']" v-if="livesDelta != null">
              {{ livesDelta >= 0 ? '▲' : '▼' }} {{ Math.abs(livesDelta).toFixed(1) }}% vs prior report
            </span>
          </div>
        </div>
        <div v-if="livesSeries.length > 1" style="min-width:0;">
          <HeroChart :series="livesSeries" format="count" />
        </div>
      </div>

      <!-- Attribution panel selector -->
      <div v-if="reportDts.length" style="margin-bottom:8px;">
        <label style="font-size:12px; color:#6B7280; margin-right:6px;">Attribution Panel</label>
        <select :value="selectedReportDt" @change="$emit('update:selected-report-dt', $event.target.value)" style="font-size:12px; padding:2px 6px; border:1px solid #D1D5DB; border-radius:4px;">
          <option v-for="d in reportDts" :key="d" :value="d">{{ d }}</option>
        </select>
      </div>

      <!-- Metric table -->
      <div v-if="table.length" class="ext-table-wrap">
        <table class="ext-table">
          <thead>
            <tr>
              <th style="padding-left:16px;">Metric</th>
              <th class="num">YTD</th>
              <th class="num">Rolling 12 Mo</th>
              <th class="num">E&amp;U Benchmark</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(row, i) in table" :key="i">
              <tr v-if="row.group" class="group-header"><td colspan="4">{{ row.label }}</td></tr>
              <tr v-else>
                <td class="metric-name">{{ row.label }}</td>
                <td class="num">{{ fmtVal(row.ytd, row.format) }}</td>
                <td class="num">{{ fmtVal(row.rolling12, row.format) }}</td>
                <td class="num">{{ fmtVal(row.beur, row.format) }}</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div class="mt-8"><AIInsights :entity-id="entityId" /></div>
    </div>
    <div v-else class="flex items-center justify-center py-20" style="color:#A5A5A6; font-size:13px;">No data available</div>
  </div>
</template>

<script>
import CCLFReport_EntityCost from './CCLFReport_EntityCost.vue'
import AIInsights from '@/components/InsightsNew/AIInsights.vue'
import ExternalFocusItems from '../ExternalFocusItems.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'

const HeroChart = {
  name: 'HeroChart',
  props: { series: { type: Array, required: true }, format: { type: String, default: 'currency' }, color: { type: String, default: '#F76734' } },
  data() { return { hoverIdx: null, containerW: 280 } },
  mounted() { this.onResize(); this._ro = new ResizeObserver(() => this.onResize()); this._ro.observe(this.$el.parentElement) },
  beforeUnmount() { this._ro?.disconnect() },
  computed: {
    W() { return this.containerW }, H() { return 140 },
    pad() { return { top: 16, right: 12, bottom: 22, left: 62 } },
    values() { return this.series.map(s => s.value).filter(v => v != null) },
    yMin() { const min = Math.min(...this.values), range = Math.max(...this.values) - min || 1; return Math.max(0, min - range * 0.3) },
    yMax() { const max = Math.max(...this.values), range = max - Math.min(...this.values) || 1; return max + range * 0.15 },
    yTicks() { const range = this.yMax - this.yMin; let step = Math.pow(10, Math.floor(Math.log10(range))); const t = []; let v = Math.ceil(this.yMin / step) * step; while (v <= this.yMax) { t.push(v); v += step } if (t.length < 3) { const h = step / 2; t.length = 0; v = Math.ceil(this.yMin / h) * h; while (v <= this.yMax) { t.push(v); v += h } } return t },
    plotW() { return this.W - this.pad.left - this.pad.right },
    plotH() { return this.H - this.pad.top - this.pad.bottom },
    points() { return this.series.map((s, i) => ({ x: this.pad.left + (i / Math.max(1, this.series.length - 1)) * this.plotW, y: this.pad.top + (1 - (s.value - this.yMin) / (this.yMax - this.yMin)) * this.plotH, val: s.value, date: s.date, idx: i })) },
    linePath() { return this.points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ') },
  },
  methods: {
    onResize() { if (this.$el?.parentElement) this.containerW = this.$el.parentElement.clientWidth },
    yPos(v) { return this.pad.top + (1 - (v - this.yMin) / (this.yMax - this.yMin)) * this.plotH },
    fmtTick(v) { return this.format === 'count' ? Math.round(v).toLocaleString('en-US') : '$' + Math.round(v).toLocaleString('en-US') },
    fmtFull(d) { if (!d) return ''; const [y, m] = d.split('-'); const M = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return `${M[+m]} '${y.slice(2)}` },
    showXLabel(i) { const n = this.series.length; if (n <= 7) return true; if (i === n - 1) return true; const m = +this.series[i]?.date?.split('-')[1]; return n <= 18 ? m % 3 === 1 : (m === 1 || m === 7) },
    xLabel(d) { if (!d) return ''; const [y, m] = d.split('-'); const M = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return `${M[+m]} '${y.slice(2)}` },
  },
  template: `<svg :viewBox="'0 0 ' + W + ' ' + H" style="width:100%; height:auto;" class="overflow-visible">
    <template v-for="tick in yTicks" :key="tick"><line :x1="pad.left" :x2="W - pad.right" :y1="yPos(tick)" :y2="yPos(tick)" stroke="#E8E9E9" stroke-width="1" /><text :x="pad.left - 8" :y="yPos(tick) + 3" text-anchor="end" style="font-size:9px; fill:#A5A5A6;">{{ fmtTick(tick) }}</text></template>
    <path :d="linePath" fill="none" :stroke="color" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
    <circle v-for="p in points" :key="p.idx" :cx="p.x" :cy="p.y" r="3" :fill="hoverIdx === p.idx ? color : '#FFFFFF'" :stroke="color" stroke-width="1.5" class="cursor-pointer" @mouseenter="hoverIdx = p.idx" @mouseleave="hoverIdx = null" />
    <text v-for="(p, i) in points" :key="'x'+i" v-show="showXLabel(i)" :x="p.x" :y="H - 4" text-anchor="middle" style="font-size:9px; fill:#A5A5A6;">{{ xLabel(p.date) }}</text>
    <g v-if="hoverIdx != null && points[hoverIdx]"><rect :x="points[hoverIdx].x - 40" :y="points[hoverIdx].y - 28" width="80" height="20" rx="4" fill="#1E1F21" fill-opacity="0.9" /><text :x="points[hoverIdx].x" :y="points[hoverIdx].y - 15" text-anchor="middle" style="font-size:10px; fill:#FFFFFF; font-weight:500;">{{ fmtFull(points[hoverIdx].date) }} · {{ fmtTick(points[hoverIdx].val) }}</text></g>
  </svg>`,
}

export default {
  name: 'CCLFReport_SummaryAnalysis',
  components: { HeroChart, CCLFReport_EntityCost, AIInsights, ExternalFocusItems, LoadingIndicator },
  props: {
    entityId: { type: Number, default: null },
    performanceYear: { type: Number, default: null },
    loading: { type: Boolean, default: false },
    summaryData: { type: Object, default: null },  // {chart, tables, max_efctv_dt}
    attributionSeries: { type: Array, default: () => [] },
    reportDts: { type: Array, default: () => [] },
    selectedReportDt: { type: String, default: '2026-03-01' },
    scopeType: { type: String, default: '' },
    scopeEntityId: { type: Number, default: null },
    scopeEntities: { type: Array, default: () => [] },
    careSetting: { type: String, default: '' },
  },
  emits: ['update:scope-type', 'update:scope-entity-id', 'update:care-setting', 'update:selected-report-dt'],
  data() { return { hiddenSeriesKeys: {} } },

  computed: {
    chart() { return this.summaryData?.chart || [] },
    tables() { return this.summaryData?.tables || {} },
    table() { return this.tables[this.selectedReportDt] || [] },
    maxEfctvDt() { return this.summaryData?.max_efctv_dt || null },

    // Shape for CCLFReport_EntityCost chart
    entityCostPbpm() {
      if (!this.chart.length) return null
      return {
        current_year: this.chart.map(c => ({
          CLM_FROM_DT: c.month, CLM_THRU_DT: c.month,
          PBPM: c.pbpm,
          ROLLING_12_PBPM: c.r12_pbpm,
          BEUR_PBPM: c.beur_pbpm,
          ATTRIBUTION_COUNT: c.patients,
        })),
        prior_year: [],
        current_py: this.performanceYear ? this.performanceYear - 1 : 2025,
        prior_py: null,
      }
    },

    // Hero: compare last two chart points' BEUR (or PBPM if no BEUR)
    heroChange() {
      const c = this.chart
      if (c.length < 2) return null
      // Find last two distinct BEUR values
      const withBeur = c.filter(r => r.beur_pbpm != null)
      const distinct = []
      for (const r of withBeur) {
        if (!distinct.length || Math.abs(r.beur_pbpm - distinct[distinct.length - 1].beur_pbpm) > 0.01)
          distinct.push(r)
      }
      if (distinct.length < 2) return null
      const latest = distinct[distinct.length - 1].beur_pbpm
      const prior = distinct[distinct.length - 2].beur_pbpm
      return prior ? ((latest - prior) / Math.abs(prior)) * 100 : null
    },

    legendItems() {
      return [
        { key: 'Monthly PBPM', label: 'Monthly PBPM', color: 'rgba(75, 192, 192, 1)', dashed: false },
        { key: 'Rolling 12mo PBPM', label: 'Rolling 12mo PBPM', color: 'rgba(30, 64, 175, 1)', dashed: true },
        { key: 'BEUR PBPM', label: 'E&U PBPM', color: 'rgba(239, 68, 68, 1)', dashed: false },
      ]
    },
    hiddenSeriesSet() { const s = new Set(); for (const [k, v] of Object.entries(this.hiddenSeriesKeys)) { if (v) s.add(k) } return s },
    hiddenSeriesList() { return [...this.hiddenSeriesSet] },

    livesSeries() { return this.attributionSeries },
    livesCurrentValue() { const s = this.livesSeries; return s.length ? s[s.length - 1].value : null },
    livesDelta() {
      const s = this.livesSeries
      if (s.length < 2) return null
      const c = s[s.length - 1].value, p = s[s.length - 2].value
      return (c != null && p != null && p !== 0) ? ((c - p) / Math.abs(p)) * 100 : null
    },
  },

  methods: {
    toggleSeries(key) { this.hiddenSeriesKeys = { ...this.hiddenSeriesKeys, [key]: !this.hiddenSeriesKeys[key] } },
    fmtVal(val, fmt) {
      if (val == null) return '—'
      if (fmt === 'currency') return '$' + Math.round(val).toLocaleString('en-US')
      if (fmt === 'count') return Number(val).toLocaleString('en-US', { maximumFractionDigits: 1 })
      if (fmt === 'per_1k') return Number(val).toFixed(1)
      return String(val)
    },
    fmtCount(v) { return v != null ? Math.round(v).toLocaleString('en-US') : '—' },
  },
}
</script>
